/**
 * 环境
 */
import { getApp } from '@mx/util/get-app';
import { getSystem } from '@mx/util/get-system';
import { getUA } from '@mx/util/get-ua';
import { getAllCookie } from '@mx/util/get-all-cookie';
import { getAllQueryString, QueryStrings } from '@mx/util/get-all-query-string';
import envRoot from '@mx/fsp-mweb-env';
import { bridge } from './bridge';

export enum Runtime {
  Prod,
  Staging,
  Test,
  Dev,
  Local
}

class Env {
  system: string;
  isIOS: boolean;
  isAndroid: boolean;
  osVersion: string;

  appName: string;
  isInConch: boolean;
  isInWaimai: boolean;
  isInMeituan: boolean;
  isInDianping: boolean;
  isInDaxiang: boolean;
  isInMaicai: boolean;
  isInMovie: boolean;
  isInYouXuan: boolean;
  isInWeixin: boolean;

  runtime: Runtime;
  pgyEnv: string;

  readonly isNative: boolean = /titans(no)?x/i.test(navigator.userAgent);
  queryStrings: QueryStrings;

  // 业务参数
  businessParams: {
    actName: string; // dcep活动名称
    actId: string; //活动id
    actGroupName: string; // 活动组名称
    from: string; // 渠道来源
    theme: string; // 主题参数
    payType: string; // 银行编码
  };
  // common params
  entry: string;
  commonParams: {
    cityId: string | number;
    platform: string;
    app: string;
    entry: string;
    token: string;
    uuid: string;
    version: string;
    ci: string;
    fingerprint?: string;
    location?: string;
  };

  // 跳转链接
  couponUrl: string;

  // 微信小程序钱包浮层
  layerType: string;

  fupAppType: string;

  // 数币专区地址
  ecnyHomeUrl: string;

  // 猫眼首页
  movieIndex: string;

  // portm
  portmOrigin: string;

  constructor() {
    const cookies = getAllCookie();
    const urlParams = getAllQueryString();
    this.queryStrings = getAllQueryString();

    const getParam = (...names: string[]) => {
      for (let i = 0, len = names.length; i < len; i++) {
        const name = names[i];
        const param = cookies[name] || urlParams[name];
        if (param) {
          return param;
        }
      }
      return '';
    };

    if (/\.dev\.sankuai\.com/.test(location.host)) {
      this.runtime = Runtime.Dev;
      this.pgyEnv = 'test';
    } else if (/\.test\.sankuai\.com/.test(location.host)) {
      this.runtime = Runtime.Test;
      this.pgyEnv = 'test';
    } else if (/\.st\.(sankuai|meituan)\.com/.test(location.host)) {
      this.runtime = Runtime.Staging;
      this.pgyEnv = 'st';
    } else if (/\.meituan\.com/.test(location.host)) {
      this.runtime = Runtime.Prod;
      this.pgyEnv = 'prod';
    } else {
      this.runtime = Runtime.Local;
      this.pgyEnv = 'test';
    }

    // 初始化券页面url
    this.couponUrl = 'https://npay.meituan.com/resource/promotion-gathering/index.html?scene=lvseyixia';
    if (this.runtime === Runtime.Test) {
      this.couponUrl = 'http://stable.pay.test.sankuai.com/resource/promotion-gathering/index.html?scene=lvseyixia';
    }
    if (this.runtime === Runtime.Staging) {
      this.couponUrl = 'https://stable-pay.st.meituan.com/resource/promotion-gathering/index.html?scene=lvseyixia';
    }
    if (this.runtime === Runtime.Prod) {
      this.couponUrl = 'https://npay.meituan.com/resource/promotion-gathering/index.html?scene=lvseyixia';
    }

    // 初始化数币专区url
    this.ecnyHomeUrl = 'https://ecny.meituan.com/ecny/home/index.html';
    if (this.runtime === Runtime.Staging) {
      this.ecnyHomeUrl = 'http://stable.pay.st.sankuai.com/ecny/home/index.html';
    }
    if (this.runtime === Runtime.Test) {
      this.ecnyHomeUrl = 'http://stable.pay.test.sankuai.com/ecny/home/index.html';
    }
    if (this.runtime === Runtime.Prod) {
      this.ecnyHomeUrl = 'https://ecny.meituan.com/ecny/home/index.html';
    }

    // 初始化portm
    this.portmOrigin = 'https://portal-portm.meituan.com/dcepfe/config';
    if (this.runtime !== Runtime.Prod) {
      this.portmOrigin = 'https://portal.fe.test.sankuai.com/dcepfe/config';
    }

    // url params
    this.entry = urlParams.entry;
    this.layerType = urlParams.layerType;
    this.fupAppType = urlParams.fupAppType;
    this.businessParams = {
      actName: urlParams.actName,
      actId: urlParams.actId,
      actGroupName: urlParams.actGroupName,
      from: urlParams.from,
      theme: urlParams.theme,
      payType: urlParams.payType
    };
    // app
    this.system = getSystem();
    this.isIOS = this.system === 'ios';
    this.isAndroid = this.system === 'android';
    const uaInfo = getUA();
    const { osVersion } = uaInfo;
    this.osVersion = osVersion;

    this.appName = getApp(urlParams.utm_campaign);
    this.isInConch = this.appName === 'conch'; // 美团支付
    this.isInWaimai = this.appName === 'waimai';
    this.isInMeituan = this.appName === 'group';
    this.isInDianping = this.appName === 'dianping-nova';
    this.isInDaxiang = this.appName === 'daxiang';
    this.isInMaicai = this.appName === 'mtmaicai';
    this.isInMovie = this.appName === 'movie';
    this.isInYouXuan = this.appName === 'meituanyouxuan_app';
    this.isInWeixin = this.appName === 'weixin';

    this.commonParams = {
      entry: this.entry,
      cityId: getParam('ci', 'cityid') || 0,
      token: getParam('token', 'mt_c_token', 'lt', 'oops', 'thirdlogin_token'),
      ci: getParam('ci'),
      uuid: getParam('iuuid', 'uuid', 'dpid'),
      app: this.appName,
      version: getParam('version_name', 'wm_appversion'),
      platform: 'touch',
      fingerprint: '',
      location: ''
    };

    // 初始化猫眼首页地址
    if (this.isIOS) {
      this.movieIndex = 'imeituan://www.meituan.com/deal/movieshow?utm_source=group_position';
    } else {
      this.movieIndex = 'imeituan://www.meituan.com/movie/mainpage?utm_source=group_position';
    }

    // 获取定位信息
    const latAndLon = (getParam('latlon') || '').split(',');
    if (latAndLon.length === 1) { // 兼容安卓获取定位信息方式
      const lat = getParam('lat') || 0;
      const lon = getParam('lng') || 0;
      this.commonParams.location = `${lat}_${lon}`;
    } else {
      const [lat = '0', lon = '0'] = latAndLon;
      this.commonParams.location = `${lat || 0}_${lon}`;
    }

    // 获取风控指纹信息
    if (!envRoot.isHarmony) {
      bridge.getFingerprint().then((fingerprint: string) => {
        this.commonParams.fingerprint = fingerprint || '';
      }).catch((error: any) => {
        console.log('getFingerprint', error);
      });
    }
  }
}
// 兼容早期的业务参数取数位置
const initEnv = new Env();
export const env = Object.assign(initEnv, { ...initEnv.businessParams }, envRoot);
